import { useState } from 'react';
import { DeletionJobStatus } from './DeletionJobStatus';
import { DeletionConfirmDialog } from './DeletionConfirmDialog';
import { useDeleteVersion } from './useDeleteVersion';
import { protectionLabels, versionErrorCopy, versionStateLabels } from './versionCopy';
import { generationTime } from './generationCopy';
import { canDeleteVersion } from './versionTypes';
import { useCurrentMap } from './useGenerationQueries';
import { useVersionState } from './useVersionQueries';

export function VersionPanel({ version }: { version: string }) {
  const query = useVersionState(version);
  const current = useCurrentMap();
  const isPublished = current.isSuccess && current.data?.version === version;
  const [open, setOpen] = useState(false);
  const deletion = useDeleteVersion(version);
  const mutation = deletion.mutation;
  const state = query.isSuccess ? query.data : undefined;
  const stateEligible = !!state && canDeleteVersion(state) && !query.isFetching && !isPublished;
  const eligible = stateEligible && deletion.canReplace && !deletion.storageFailed;
  const canReplay = deletion.isReplay && !deletion.pending && !deletion.storageFailed && !deletion.retryUnavailable && (!deletion.rejected || stateEligible);
  const confirm = async (): Promise<void> => {
    if (!eligible && !canReplay) return;
    await deletion.confirm();
    setOpen(false);
  };
  return (
    <section className="map-generations__panel" aria-label="Состояние версии">
      <h3>Состояние версии</h3>
      <p className="map-generations__identifier">{version}</p>
      {query.isPending && <p role="status">Проверка версии…</p>}
      {query.isError && <p className="admin-message admin-message_error" role="alert">{versionErrorCopy(query.error)}</p>}
      <button className="workspace-secondary-button" type="button" disabled={query.isFetching} onClick={() => { deletion.resetError(); void query.refetch(); }}>Обновить состояние версии</button>
      {state && (
        <>
          <p>{versionStateLabels[state.state]}</p>
          {isPublished && state.protection_reason !== 'current_version' && <p>{protectionLabels.current_version}</p>}
          {state.protection_reason && <p>{protectionLabels[state.protection_reason]}</p>}
          <p>{state.deletion_enabled ? 'Удаление включено' : 'Удаление отключено или ещё не готово. Обратитесь к администратору сервиса.'}</p>
          <dl className="map-generations__facts">
            <dt>Замена версии</dt><dd>{state.retirement?.replacement_version ?? 'Не подтверждена'}</dd>
            <dt>Замена подтверждена не раньше</dt><dd>{generationTime(state.retirement?.retired_not_before ?? null)}</dd>
            <dt>Срок хранения после замены</dt><dd>{state.retention_seconds} с.</dd>
            <dt>Срок хранения истёк</dt><dd>{state.retention_eligible ? 'Да' : 'Нет или не подтверждён'}</dd>
          </dl>
          <p>Состояние справочное. Перед удалением сервис повторно проверит защиту версии.</p>
          <button className="workspace-secondary-button" type="button" disabled={!eligible || deletion.pending} onClick={() => setOpen(true)}>Удалить версию</button>
        </>
      )}
      {deletion.isOwnRequest && !deletion.request?.id && !mutation.isPending && (
        <>
          <p role="status">Результат удаления пока неизвестен. Автоматического повтора нет. Можно повторить только тот же запрос.</p>
          <button className="workspace-secondary-button" type="button" disabled={!canReplay} onClick={() => setOpen(true)}>Повторить запрос удаления</button>
        </>
      )}
      {deletion.storageFailed && <p role="alert">Не удалось сохранить или восстановить подтверждение удаления. Отправка отключена. Если запрос уже отправлен, обратитесь к оператору.</p>}
      {deletion.isOwnRequest && deletion.request?.id && <DeletionJobStatus request={deletion.request} />}
      {deletion.error !== null && <p role="alert">{versionErrorCopy(deletion.error)}</p>}
      {open && <DeletionConfirmDialog version={version} pending={deletion.pending} replay={deletion.isReplay} close={() => setOpen(false)} confirm={() => { void confirm(); }} />}
    </section>
  );
}
